import { useState, useEffect } from 'react';
import api from '../lib/api';
import { CF_REQUEST_TIMEOUT_MS } from '../utils/campaignFinanceUtils';

export interface CFFilterOption {
  value: string;
  label: string;
  count: number;
}

interface ExpenditureFilterOptions {
  expenditureTypes: CFFilterOption[];
  paymentTypes: CFFilterOption[];
  disbursementTypes: CFFilterOption[];
  loading: boolean;
}

const mapList = (list: unknown): CFFilterOption[] =>
  (Array.isArray(list) ? list : [])
    .filter((o: { value?: string }) => o.value)
    .map((o: { value: string; label?: string; count?: number | string }) => ({
      value: String(o.value),
      label: String(o.label ?? o.value),
      count: Number(o.count) || 0,
    }));

/**
 * The three Expenditures-mode dropdowns in one fetch, served from cf_filter_options
 * (GET /campaign-finance/expenditure-filter-options). Mirrors useContributorTypes;
 * failures are non-fatal — the dropdowns simply show only their "All …" option.
 */
export function useExpenditureFilterOptions(): ExpenditureFilterOptions {
  const [expenditureTypes, setExpenditureTypes] = useState<CFFilterOption[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<CFFilterOption[]>([]);
  const [disbursementTypes, setDisbursementTypes] = useState<CFFilterOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/campaign-finance/expenditure-filter-options', { timeout: CF_REQUEST_TIMEOUT_MS })
      .then(res => {
        if (cancelled) return;
        const d = res.data?.data || {};
        setExpenditureTypes(mapList(d.expenditure_types));
        setPaymentTypes(mapList(d.payment_types));
        setDisbursementTypes(mapList(d.disbursement_types));
      })
      .catch(() => { /* non-fatal */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { expenditureTypes, paymentTypes, disbursementTypes, loading };
}
